import { AfterViewInit, Component, DestroyRef, effect, EffectRef, ElementRef, inject, Renderer2, Signal, ViewChild } from '@angular/core';
import { ButtonComponent } from '@components/shared/button/button.component';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { BenefitsService } from '@services/benefits.service';
import { DvmService } from '@services/dvm.service';
import { LayoutService } from '@services/layout.service';
import { MenuContainerService } from '@services/menu';
import { TranslationService } from '@services/translation.service';
import { VenueLayoutService } from '@services/venue-layout.service';
import anime from 'animejs';
import { version } from './../../../../../../package.json';

@Component({
    selector: 'app-legend',
    host: { class: 'contents' },
    imports: [
        ButtonComponent
    ],
    standalone: true,
    templateUrl: './legend.component.html',
    styleUrl: './legend.component.css'
})
export class LegendComponent implements AfterViewInit {

  @ViewChild('legendBtn', { read: ElementRef })
  private readonly legendBtn!: ElementRef<HTMLElement>;

  @ViewChild('legendContent', { static: true, read: ElementRef})
  public legendContent!: ElementRef<HTMLDivElement>;

  // SERVICES
  private readonly dvmService:   DvmService               = inject(DvmService);
  private readonly renderer:     Renderer2                = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly translation = inject(TranslationService);
  private readonly venueLayout:   VenueLayoutService       = inject(VenueLayoutService);
  private readonly benefits:      BenefitsService          = inject(BenefitsService);
  private readonly menuContainer: MenuContainerService     = inject(MenuContainerService);

  protected readonly controls = [
    { key: 'search', text: 'Find a section or seat', path: 'm21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z' },
    { key: 'home', text: 'Return to the initial map', path: 'm2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25' },
    { key: 'back', text: 'Return to the previous view', path: 'M15.75 19.5 8.25 12l7.5-7.5' },
    { key: 'fullscreen', text: 'View the 3D view in full screen', path: 'M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15' },
    { key: 'share_link', text: 'Copy a link to the current 3D view', path: 'M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244' },
  ];

  // GLOBAL STATE
  public readonly  view:         Signal<AppViews | null>  = this.dvmService.view;
  private readonly isMobile:     Signal<boolean>          = inject(LayoutService).isMobile;

  // BUTTON STYLE
  public readonly  type:         ButtonType               = ButtonType.Pill;

  // COMPONENT STATE
  private          legendVisible:    boolean              = false;
  private          containerVisible: boolean              = false;
  protected version: string = version;

  // EFFECTS
  private readonly onViewChange:  EffectRef                = effect((): void => {

    const view: AppViews | null = this.view();

    if(view === null) return;

    this.closeContainer();

  } );

  // Un solo panel abierto en el topbar: Layout, Menu o Benefits abiertos cierran la leyenda.
  private readonly closeOnOtherPanelOpened: EffectRef = effect((): void => {

    const otherOpened: boolean = this.venueLayout.showContainer() || this.benefits.showContainer() || !!this.menuContainer.showContainer();

    if(otherOpened) this.closeContainer();

  });

  public ngAfterViewInit(): void {
    this.setElementHidden(this.legendBtn.nativeElement, false);
    this.setElementHidden(this.legendContent.nativeElement, true);
    this.animateLegend(true);
    this.renderer.setAttribute(this.getNativeButton(this.legendBtn.nativeElement), 'aria-label', this.translation.getText('legend', 'help', 'Help'));
    this.renderer.setAttribute(this.getNativeButton(this.legendBtn.nativeElement), 'aria-expanded', 'false');
    this.destroyRef.onDestroy(() => {
      anime.remove(this.legendBtn.nativeElement);
      anime.remove(this.legendContent.nativeElement);
    });
  }

  private readonly helpLabel = effect(() => {
    const label = this.translation.getText('legend', 'help', 'Help');
    if (this.legendBtn) this.renderer.setAttribute(this.getNativeButton(this.legendBtn.nativeElement), 'aria-label', label);
  });

  private animateLegend(show: boolean): void {
    if(show === this.legendVisible) return;

    const legendButton = this.legendBtn.nativeElement;

    anime.remove(legendButton);
    this.legendVisible = show;

    if(show) this.setElementHidden(legendButton, false);
    
    anime({
      complete: (): void => {
        if(!show) this.setElementHidden(legendButton, true);
      },
      targets:  legendButton,
      opacity:  show ? 1 : 0,
      duration: 300,
      easing:   'easeInOutQuad',
    })

  }

  protected toggleContainer(): void {
    const show: boolean = !this.containerVisible;

    // Solo desktop: Menu ignora cierres mientras anima y Benefits no pausa su timeline; abrir dejaria dos paneles.
    // Se ignora el click en vez de deshabilitar el boton: un <button disabled> con foco lo manda al body.
    if(show && !this.isMobile() && (this.menuContainer.isAnimating() || this.benefits.isAnimating())) return;

    if(show) this.closeOtherPanels();
    this.setContainerVisible(show);
  }

  // Solo desktop: en mobile viven en el bottom sheet, cuyo overlay cubre el topbar.
  private closeOtherPanels(): void {
    if(this.isMobile()) return;

    this.venueLayout.showContainer.set(false);
    this.benefits.showContainer.set(false);
    this.menuContainer.closeMenuContainer();
  }

  private closeContainer(): void {
    this.setContainerVisible(false);
  }

  private setContainerVisible(show: boolean): void {
    if(show === this.containerVisible) return;

    const legendContent = this.legendContent.nativeElement,
          legendButton = this.getNativeButton(this.legendBtn.nativeElement);

    anime.remove(legendContent);
    this.containerVisible = show;
    this.renderer.setAttribute(legendButton, 'aria-expanded', String(show));

    if(show) {
      this.setElementHidden(legendContent, false);
      this.renderer.addClass(legendButton, 'active');
    } else {
      this.renderer.removeClass(legendButton, 'active');
    }

    anime.timeline({
      complete: (): void => { 
        if(!show) this.setElementHidden(legendContent, true);
      }, 
      duration: show ? 300 : 200,
      easing: show ? 'easeInOutQuad' : 'easeInOutCubic',
    })
    .add({
      targets: legendContent,
      opacity: show ? 1 : 0,
    })
  }

  private setElementHidden(element: HTMLElement, hidden: boolean): void {
    if(hidden) {
      this.renderer.addClass(element, 'hidden');
      this.renderer.addClass(element, 'pointer-events-none');
      return;
    }

    this.renderer.removeClass(element, 'hidden');
    this.renderer.removeClass(element, 'pointer-events-none');
  }

  private getNativeButton(host: HTMLElement): HTMLButtonElement {
    return host.querySelector('button')!;
  }

}
